import { type ZodType, type ZodTypeDef, z } from 'zod'
import type { Erc20Transfer, Hex, TransactionIndex } from '../../types'
import {
  addressPrimitiveSchema,
  bigintTextSchema,
  lowerHexSchema,
} from '../../types/primitive'
import type { HeadersInput } from '../types'
import type { BlockscoutV2Client } from './v2Client'

/** Blockscout が返す次ページのカーソル。null はリクエストから除外する。 */
const cursorSchema = z
  .record(z.union([z.string(), z.number(), z.boolean(), z.null()]))
  .nullable()
/** アドレス参照の正規化スキーマ。 */
const addressSchema = z.object({ hash: addressPrimitiveSchema })
/** 確定済みの通常取引を検証するスキーマ。 */
const transactionSchema = z.object({
  block_number: z.number().int(),
  hash: lowerHexSchema,
  timestamp: z.string().datetime(),
  raw_input: lowerHexSchema,
  value: bigintTextSchema,
})
/** ERC20・NFT を含むトークン転送のスキーマ。数量の変換は ERC20 のみ行う。 */
const transferSchema = z.object({
  block_number: z.number().int(),
  transaction_hash: lowerHexSchema,
  timestamp: z.string().datetime(),
  log_index: z.number().int(),
  from: addressSchema,
  to: addressSchema,
  token: z.object({ address_hash: addressPrimitiveSchema, type: z.string() }),
  total: z.unknown(),
})
/** ERC20 数量の生整数を検証するスキーマ。 */
const totalSchema = z.object({ value: bigintTextSchema })

/** V2 アドレス履歴の共通取得パラメータ。 */
type AddressParams = {
  client: BlockscoutV2Client
  address: Hex
  fromBlock?: number
  toBlock?: number
  headers?: HeadersInput
}

/**
 * Blockscout V2 の降順ページを、指定した block 範囲まで取得する。
 * @param params クライアント、APIパス、変換スキーマ、取得範囲、認証ヘッダー。
 * @returns 指定範囲内の確定済み履歴。繰り返すカーソルは欠落を隠さずエラーにする。
 */
export const getAddressPagesV2 = async <
  Output extends { block_number: number },
  Def extends ZodTypeDef,
  Input,
>({
  client,
  path,
  schema,
  fromBlock = 0,
  toBlock = Number.MAX_SAFE_INTEGER,
  headers,
}: {
  client: BlockscoutV2Client
  path: string
  schema: ZodType<Output, Def, Input>
  fromBlock?: number
  toBlock?: number
  headers?: HeadersInput
}): Promise<Output[]> => {
  const items: Output[] = []
  const cursors = new Set<string>()
  let query: Record<string, string> = {}
  while (true) {
    const page = await client.get({
      path,
      query,
      headers: typeof headers === 'function' ? await headers() : headers,
      resultSchema: z.object({
        items: z.array(z.unknown()),
        next_page_params: cursorSchema,
      }),
    })
    const confirmedItems: Output[] = []
    for (const raw of page.items) {
      // pending 取引は次回の同期で確定後に取得する。
      if (z.object({ block_number: z.null() }).safeParse(raw).success) continue
      confirmedItems.push(schema.parse(raw))
    }
    for (const item of confirmedItems)
      if (item.block_number >= fromBlock && item.block_number <= toBlock)
        items.push(item)
    if (
      page.next_page_params === null ||
      (confirmedItems.length > 0 &&
        confirmedItems.every((item) => item.block_number < fromBlock))
    )
      return items
    query = {}
    for (const [key, value] of Object.entries(page.next_page_params))
      if (value !== null) query[key] = String(value)
    const cursor = JSON.stringify(
      Object.entries(query).sort(([a], [b]) => a.localeCompare(b)),
    )
    if (cursors.has(cursor))
      throw new Error('Blockscout V2 pagination cursor did not advance')
    cursors.add(cursor)
  }
}

/**
 * V2 の通常取引を重複のない index に変換する。
 * @param params アドレス、block 範囲と HTTP クライアント。
 * @returns 取引解析用 index。
 */
export const getAddressTransactionIndexesV2 = async (
  params: AddressParams,
): Promise<TransactionIndex[]> => {
  const items = await getAddressPagesV2({
    ...params,
    path: `/api/v2/addresses/${params.address}/transactions`,
    schema: transactionSchema,
  })
  const indexes = new Map<string, TransactionIndex>()
  for (const item of items)
    indexes.set(item.hash, {
      hash: item.hash,
      blockNumber: item.block_number,
      timestamp: Date.parse(item.timestamp),
      input: item.raw_input,
      value: item.value,
    })
  return [...indexes.values()]
}

/**
 * V2 から ERC20 と NFT の転送 index を取得する。
 * @param params アドレス、block 範囲と HTTP クライアント。
 * @returns 重複のない取引 index と ERC20 の生数量。
 */
export const getAddressTokenTransferIndexesV2 = async (
  params: AddressParams,
): Promise<{
  indexes: TransactionIndex[]
  erc20Transfers: Erc20Transfer[]
}> => {
  const items = await getAddressPagesV2({
    ...params,
    path: `/api/v2/addresses/${params.address}/token-transfers`,
    schema: transferSchema,
  })
  const indexes = new Map<string, TransactionIndex>()
  const transfers = new Map<string, Erc20Transfer>()
  for (const item of items) {
    indexes.set(item.transaction_hash, {
      hash: item.transaction_hash,
      blockNumber: item.block_number,
      timestamp: Date.parse(item.timestamp),
    })
    if (item.token.type === 'ERC-20')
      transfers.set(`${item.transaction_hash}:${item.log_index}`, {
        address: item.token.address_hash,
        from: item.from.hash,
        to: item.to.hash,
        logIndex: item.log_index,
        value: totalSchema.parse(item.total).value,
      })
  }
  return {
    indexes: [...indexes.values()],
    erc20Transfers: [...transfers.values()],
  }
}
